import React, { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { useVisibleSpacePaths } from "@/components/main/stores/orgContextStore";
import {
  endFilterOnTree,
  useSpaceFilterResult,
  useSpacesFilterStore,
} from "./spacesFilterStore";

/// The Spaces row while filtering: the label gives way to a caret, with no
/// field chrome, so typing reads as typing into the menu.
export const SpacesFilterField: React.FC<{
  onPick: (spaceId: string) => void;
}> = ({ onPick }) => {
  const { query, active, setQuery, setActive, close } = useSpacesFilterStore(
    useShallow((s) => ({
      query: s.query,
      active: s.active,
      setQuery: s.setQuery,
      setActive: s.setActive,
      close: s.close,
    })),
  );
  const result = useSpaceFilterResult();
  const total = useVisibleSpacePaths().length;
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    // The query lives as long as the field: a closing rail ends it too.
    return close;
  }, [close]);

  const matches = result?.matches ?? [];
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
    if (step && matches.length > 0) {
      e.preventDefault();
      setActive((active + step + matches.length) % matches.length);
    } else if (e.key === "Enter" && matches.length > 0) {
      e.preventDefault();
      endFilterOnTree();
      onPick(matches[active] ?? matches[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  return (
    <div className="flex item-height items-center pr-1">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <SearchIcon className="icon-md" />
      </span>
      <input
        ref={inputRef}
        type="text"
        aria-label="Filter spaces"
        placeholder="Filter spaces"
        autoComplete="off"
        spellCheck={false}
        value={query ?? ""}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (!query?.trim()) close();
        }}
        className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-fg-regular caret-accent outline-none placeholder:text-fg-muted"
      />
      {result && (
        <span
          aria-live="polite"
          className="shrink-0 whitespace-nowrap px-1 font-mono text-fg-muted"
          style={{
            fontSize: "var(--font-size-x-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {matches.length} of {total}
        </span>
      )}
      <TooltipButton
        title="Close filter"
        aria-label="Close filter"
        variant="contextual"
        size="xs"
        className="rail-action"
        onClick={close}
      >
        <CloseIcon fontSize="inherit" />
      </TooltipButton>
    </div>
  );
};
